import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import labOnboardingApi, { formatLabPayload, toBase64 } from "../services/labOnboardingApi";

const LOCAL_STORAGE_KEY = "lab_onboarding_form_draft";

const emptyTimingSlot = (days = []) => ({
  id: `timing-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
  days,
  opensAt: "08:00",
  closesAt: "20:00",
});

const emptyTestCity = () => ({
  id: `city-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
  city: "",
  files: [],
});

const emptyContact = () => ({
  id: `contact-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
  gender: "Male",
  salutation: "Mr.",
  firstName: "",
  lastName: "",
  designation: "",
  mobileNumber: "",
  email: "",
  dob: "",
  source: "",
  qualification: "",
  panNumber: "",
  panDocument: null,
});

const defaultInitialForm = {
  basic: {
    labName: "",
    mobileNumber: "",
    receptionNumber: "",
    address: "",
    area: "",
    city: "",
    samplifyCityCode: "",
    state: "",
    stateCode: "",
    pinCode: "",
    latitude: "",
    longitude: "",
    location: "",
    paymentPeriod: "monthly",
    agreementDate: "",
    terminationNoticePeriod: "30",
    paymentReconciliation: "30",
    description: "",
    gstNumber: "",
    nablNumber: "",
    panNumber: "",
    isCureBayLabCenter: "no",
    type: "",
    // Documents: { name, file, url, previewUrl, attachmentId }
    agreementCopy: null,
    bankProof: null,
    labLogo: null,
    kycDocument: null,
    panDocument: null,
    nablDocument: null,
    gstDocument: null,
  },
  testCities: [emptyTestCity()],
  timings: [emptyTimingSlot(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"])],
  contacts: [emptyContact()],
  bank: {
    bankName: "",
    accountName: "",
    accountNumber: "",
    ifscCode: "",
    location: "",
  },
  notes: "",
  remarks: "Submitted from the new Lab onboarding screen",
};

const loadPersistedForm = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return defaultInitialForm;
    const parsed = JSON.parse(raw);
    return {
      ...defaultInitialForm,
      ...parsed,
      basic: {
        ...defaultInitialForm.basic,
        ...(parsed.basic || {}),
      },
      timings: Array.isArray(parsed.timings) && parsed.timings.length > 0 ? parsed.timings : [emptyTimingSlot()],
      contacts: Array.isArray(parsed.contacts) && parsed.contacts.length > 0 ? parsed.contacts : [emptyContact()],
      testCities: Array.isArray(parsed.testCities) && parsed.testCities.length > 0 ? parsed.testCities : [emptyTestCity()],
      bank: {
        ...defaultInitialForm.bank,
        ...(parsed.bank || {}),
      },
    };
  } catch (e) {
    console.warn("Failed to load persisted lab onboarding form:", e);
    return defaultInitialForm;
  }
};

const saveToLocalStorage = (form, draftKey) => {
  try {
    const cleanBasic = { ...form.basic };
    Object.keys(cleanBasic).forEach((key) => {
      if (cleanBasic[key] instanceof File) {
        cleanBasic[key] = { name: cleanBasic[key].name, size: cleanBasic[key].size };
      }
    });
    const toSave = {
      ...form,
      basic: cleanBasic,
      draftKey: draftKey || undefined,
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn("Failed to persist lab onboarding form:", e);
  }
};

// ---------------------------------------------------------------------
// Helper: Upload all pending form attachments for a given draftKey
// ---------------------------------------------------------------------
async function uploadFormAttachments(draftKey, form) {
  if (!draftKey || !form) return;

  const uploads = [];

  // Step 7 Legal Documents
  const step7Docs = [
    { key: "agreementCopy", description: "Agreement Copy" },
    { key: "bankProof", description: "Cancelled Cheque" },
    { key: "labLogo", description: "Lab Logo" },
    { key: "kycDocument", description: "KYC Document" },
    { key: "nablDocument", description: "NABL Certificate" },
    { key: "gstDocument", description: "GST Certificate" },
  ];

  for (const item of step7Docs) {
    const doc = form.basic?.[item.key];
    const fileObj = doc?.file || (doc instanceof File ? doc : null);
    if (fileObj && fileObj instanceof File) {
      uploads.push(
        labOnboardingApi.uploadDraftAttachment({
          draftKey,
          stepNo: 7,
          description: item.description,
          file: fileObj,
          fileName: doc?.name || fileObj.name,
          contentType: doc?.type || fileObj.type,
          targetAttachmentId: doc?.attachmentId,
        })
      );
    }
  }

  // Step 2 Contact PAN Documents
  (form.contacts || []).forEach((c) => {
    const doc = c.panDocument;
    const fileObj = doc?.file || (doc instanceof File ? doc : null);
    if (fileObj && fileObj instanceof File) {
      uploads.push(
        labOnboardingApi.uploadDraftAttachment({
          draftKey,
          stepNo: 2,
          description: `Contact PAN Card - ${c.id}`,
          file: fileObj,
          fileName: doc?.name || fileObj.name,
          contentType: doc?.type || fileObj.type,
          targetAttachmentId: doc?.attachmentId,
        })
      );
    }
  });

  // Step 3 Test City Price Lists
  (form.testCities || []).forEach((tc) => {
    (tc.files || []).forEach((f) => {
      const fileObj = f?.file || (f instanceof File ? f : null);
      if (fileObj && fileObj instanceof File) {
        uploads.push(
          labOnboardingApi.uploadDraftAttachment({
            draftKey,
            stepNo: 3,
            description: `Test Menu - ${tc.city || "default"} - ${fileObj.name}`,
            file: fileObj,
            fileName: fileObj.name,
            contentType: fileObj.type,
            targetAttachmentId: f?.attachmentId,
          })
        );
      }
    });
  });

  if (uploads.length > 0) {
    await Promise.allSettled(uploads);
  }
}

// ---------------------------------------------------------------------
// Async Thunks
// ---------------------------------------------------------------------

/**
 * Save / Update draft on server
 */
export const saveLabDraftServer = createAsyncThunk(
  "labOnboarding/saveLabDraftServer",
  async ({ remarks } = {}, { getState, rejectWithValue }) => {
    try {
      const { form, draftKey } = getState().labOnboarding;
      let activeDraftKey = draftKey;

      if (!activeDraftKey) {
        const res = await labOnboardingApi.saveNewLabDraft({ form, remarks });
        activeDraftKey = res?.data?.draftKey || res?.data?.data?.draftKey;
      } else {
        await labOnboardingApi.updateSavedLabDraft({ draftKey: activeDraftKey, form, remarks });
      }

      if (activeDraftKey) {
        await uploadFormAttachments(activeDraftKey, form);
      }

      return { draftKey: activeDraftKey };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Failed to save draft");
    }
  }
);

/**
 * Resume saved draft from server
 */
export const resumeLabDraftServer = createAsyncThunk(
  "labOnboarding/resumeLabDraftServer",
  async (draftKey, { rejectWithValue }) => {
    try {
      const [draftRes, attachmentsRes] = await Promise.allSettled([
        labOnboardingApi.resumeLabDraft(draftKey),
        labOnboardingApi.listDraftAttachments(draftKey),
      ]);

      const draftData = draftRes.status === "fulfilled" ? draftRes.value.data : null;
      if (!draftData) throw new Error("Could not resume draft from server");

      const attachments =
        attachmentsRes.status === "fulfilled" && Array.isArray(attachmentsRes.value?.data)
          ? attachmentsRes.value.data
          : Array.isArray(draftData.attachments)
          ? draftData.attachments
          : [];

      return { draftData, attachments, draftKey };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Failed to resume draft");
    }
  }
);

/**
 * Submit draft for approval
 */
export const submitLabDraftServer = createAsyncThunk(
  "labOnboarding/submitLabDraftServer",
  async ({ remarks } = {}, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().labOnboarding;
      let activeDraftKey = state.draftKey;

      // 1. Ensure draft is saved first
      if (!activeDraftKey) {
        const draftRes = await labOnboardingApi.saveNewLabDraft({ form: state.form, remarks });
        activeDraftKey = draftRes?.data?.draftKey || draftRes?.data?.data?.draftKey;
      } else {
        await labOnboardingApi.updateSavedLabDraft({ draftKey: activeDraftKey, form: state.form, remarks });
      }

      if (!activeDraftKey) {
        throw new Error("Could not acquire draftKey for submission");
      }

      // 2. Upload pending files
      await uploadFormAttachments(activeDraftKey, state.form);

      // 3. Submit
      const submitRes = await labOnboardingApi.submitSavedLabDraft({
        draftKey: activeDraftKey,
        form: state.form,
        remarks,
      });

      const data = submitRes?.data || {};
      return {
        centerCode: data.centerCode || data.code || data.data?.centerCode || data.data?.code || "LAB000001",
        draftKey: activeDraftKey,
        data,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Failed to submit lab onboarding");
    }
  }
);

// ---------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------
const savedData = loadPersistedForm();
const initialState = {
  form: savedData,
  draftKey: savedData.draftKey || localStorage.getItem("lab_draft_key") || null,
  submittedCenterCode: null,
  status: "idle", // 'idle' | 'saving' | 'submitting' | 'submitted' | 'error'
  lastSavedAt: null,
  error: null,
};

const labOnboardingSlice = createSlice({
  name: "labOnboarding",
  initialState,
  reducers: {
    setBasicField(state, action) {
      const { field, value } = action.payload;
      state.form.basic[field] = value;
      saveToLocalStorage(state.form, state.draftKey);
    },
    setBasicFields(state, action) {
      state.form.basic = { ...state.form.basic, ...action.payload };
      saveToLocalStorage(state.form, state.draftKey);
    },
    setDocument(state, action) {
      const { docKey, docData } = action.payload;
      state.form.basic[docKey] = docData;
      saveToLocalStorage(state.form, state.draftKey);
    },
    deleteDocument(state, action) {
      const { docKey } = action.payload;
      state.form.basic[docKey] = null;
      saveToLocalStorage(state.form, state.draftKey);
    },
    // Timings
    setTimings(state, action) {
      state.form.timings = action.payload;
      saveToLocalStorage(state.form, state.draftKey);
    },
    addTimingSlot(state) {
      const firstSlot = state.form.timings[0] || {};
      const newSlot = {
        id: `timing-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
        days: [],
        opensAt: firstSlot.opensAt || "08:00",
        closesAt: firstSlot.closesAt || "20:00",
      };
      state.form.timings.push(newSlot);
      saveToLocalStorage(state.form, state.draftKey);
    },
    updateTimingSlot(state, action) {
      const { id, field, value } = action.payload;
      state.form.timings = state.form.timings.map((t) => (t.id === id ? { ...t, [field]: value } : t));
      saveToLocalStorage(state.form, state.draftKey);
    },
    removeTimingSlot(state, action) {
      state.form.timings = state.form.timings.filter((t) => t.id !== action.payload);
      saveToLocalStorage(state.form, state.draftKey);
    },
    // Contacts
    addContact(state) {
      state.form.contacts.push(emptyContact());
      saveToLocalStorage(state.form, state.draftKey);
    },
    updateContact(state, action) {
      const { id, field, value } = action.payload;
      state.form.contacts = state.form.contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c));
      saveToLocalStorage(state.form, state.draftKey);
    },
    removeContact(state, action) {
      state.form.contacts = state.form.contacts.filter((c) => c.id !== action.payload);
      saveToLocalStorage(state.form, state.draftKey);
    },
    // Test Cities
    addTestCity(state) {
      state.form.testCities.push(emptyTestCity());
      saveToLocalStorage(state.form, state.draftKey);
    },
    updateTestCity(state, action) {
      const { id, field, value } = action.payload;
      state.form.testCities = state.form.testCities.map((tc) => (tc.id === id ? { ...tc, [field]: value } : tc));
      saveToLocalStorage(state.form, state.draftKey);
    },
    removeTestCity(state, action) {
      state.form.testCities = state.form.testCities.filter((tc) => tc.id !== action.payload);
      saveToLocalStorage(state.form, state.draftKey);
    },
    // Bank
    setBankField(state, action) {
      const { field, value } = action.payload;
      state.form.bank[field] = value;
      saveToLocalStorage(state.form, state.draftKey);
    },
    // Notes & Remarks
    setNotes(state, action) {
      state.form.notes = action.payload;
      saveToLocalStorage(state.form, state.draftKey);
    },
    setRemarks(state, action) {
      state.form.remarks = action.payload;
      saveToLocalStorage(state.form, state.draftKey);
    },
    setDraftKey(state, action) {
      state.draftKey = action.payload;
      if (action.payload) {
        localStorage.setItem("lab_draft_key", action.payload);
      } else {
        localStorage.removeItem("lab_draft_key");
      }
      saveToLocalStorage(state.form, state.draftKey);
    },
    // Draft & Reset
    saveDraft(state) {
      saveToLocalStorage(state.form, state.draftKey);
      state.lastSavedAt = new Date().toISOString();
    },
    resetForm(state) {
      state.form = {
        ...defaultInitialForm,
        timings: [emptyTimingSlot(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"])],
        contacts: [emptyContact()],
        testCities: [emptyTestCity()],
      };
      state.draftKey = null;
      state.submittedCenterCode = null;
      state.status = "idle";
      state.error = null;
      state.lastSavedAt = null;
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      localStorage.removeItem("lab_draft_key");
    },
    setSubmitStatus(state, action) {
      state.status = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Save Draft Server
      .addCase(saveLabDraftServer.pending, (state) => {
        state.status = "saving";
        state.error = null;
      })
      .addCase(saveLabDraftServer.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload?.draftKey) {
          state.draftKey = action.payload.draftKey;
          localStorage.setItem("lab_draft_key", action.payload.draftKey);
        }
        state.lastSavedAt = new Date().toISOString();
        saveToLocalStorage(state.form, state.draftKey);
      })
      .addCase(saveLabDraftServer.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
      })
      // Resume Draft Server
      .addCase(resumeLabDraftServer.fulfilled, (state, action) => {
        const { draftData, attachments, draftKey } = action.payload;
        state.draftKey = draftKey;
        localStorage.setItem("lab_draft_key", draftKey);

        const serverForm = draftData.form || draftData;
        if (serverForm.basic) {
          state.form.basic = {
            ...state.form.basic,
            ...serverForm.basic,
          };
        }
        if (Array.isArray(serverForm.timings) && serverForm.timings.length) {
          state.form.timings = serverForm.timings;
        }
        if (Array.isArray(serverForm.contacts) && serverForm.contacts.length) {
          state.form.contacts = serverForm.contacts;
        }
        if (Array.isArray(serverForm.testCities) && serverForm.testCities.length) {
          state.form.testCities = serverForm.testCities;
        }
        if (serverForm.bank) {
          state.form.bank = { ...state.form.bank, ...serverForm.bank };
        }
        if (serverForm.notes !== undefined) {
          state.form.notes = serverForm.notes;
        }

        // Map server attachments back into form
        if (Array.isArray(attachments)) {
          attachments.forEach((att) => {
            const desc = att.description || "";
            const viewUrl = att.viewUrl || att.url || att.previewUrl || att.fileUrl || "";
            const fileData = {
              attachmentId: att.id,
              name: att.fileName || "document",
              url: viewUrl,
              previewUrl: viewUrl,
              description: desc,
            };

            if (desc.includes("Agreement Copy")) state.form.basic.agreementCopy = fileData;
            else if (desc.includes("Cancelled Cheque")) state.form.basic.bankProof = fileData;
            else if (desc.includes("Lab Logo")) state.form.basic.labLogo = fileData;
            else if (desc.includes("KYC Document") || desc.includes("KYC Pan") || desc.includes("KYC PAN")) state.form.basic.kycDocument = fileData;
            else if (desc.includes("NABL Certificate")) state.form.basic.nablDocument = fileData;
            else if (desc.includes("GST Certificate")) state.form.basic.gstDocument = fileData;
            else if (desc.startsWith("Contact PAN Card - ")) {
              const contactId = desc.replace("Contact PAN Card - ", "").trim();
              const c = state.form.contacts.find((x) => x.id === contactId);
              if (c) c.panDocument = fileData;
            } else if (desc.startsWith("Test Menu - ")) {
              const parts = desc.split(" - ");
              const cityName = parts[1]?.trim();
              if (cityName) {
                const tc = state.form.testCities.find((x) => x.city?.toLowerCase() === cityName.toLowerCase());
                if (tc) {
                  if (!Array.isArray(tc.files)) tc.files = [];
                  tc.files.push(fileData);
                }
              }
            }
          });
        }

        saveToLocalStorage(state.form, state.draftKey);
      })
      // Submit Draft Server
      .addCase(submitLabDraftServer.pending, (state) => {
        state.status = "submitting";
        state.error = null;
      })
      .addCase(submitLabDraftServer.fulfilled, (state, action) => {
        state.status = "submitted";
        state.submittedCenterCode = action.payload.centerCode;
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        localStorage.removeItem("lab_draft_key");
      })
      .addCase(submitLabDraftServer.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
      });
  },
});

export const {
  setBasicField,
  setBasicFields,
  setDocument,
  deleteDocument,
  setTimings,
  addTimingSlot,
  updateTimingSlot,
  removeTimingSlot,
  addContact,
  updateContact,
  removeContact,
  addTestCity,
  updateTestCity,
  removeTestCity,
  setBankField,
  setNotes,
  setRemarks,
  setDraftKey,
  saveDraft,
  resetForm,
  setSubmitStatus,
} = labOnboardingSlice.actions;

export default labOnboardingSlice.reducer;
